type DescriptionResolver = (pathname: string, search: string) => string | null;

/**
 * Singleton that holds the current "activity id" + a friendly description
 * of what the user is doing. The id flows out as `X-Activity-Id` on every
 * outbound API call; the description flows out as `X-Activity-Description`
 * when one is set. The backend's access logger dedupes by activity id so
 * a single page navigation produces a single access_logs row labelled
 * with the description.
 *
 * Rotation is lazy and path-driven: every `getCurrent()` /
 * `getCurrentDescription()` call re-checks `window.location.pathname` and
 * rotates the id (and re-resolves the description via the registered
 * resolver) when the path has changed since the last check. This avoids
 * an effect-ordering race where child components fire API calls before a
 * parent-level route-change hook gets a chance to update state — without
 * lazy rotation, the first request after a navigation would go out with
 * the previous page's description (or no description at all on initial
 * boot), and the resulting access_logs row would be labelled with a
 * URL-derived fallback like "Get Saved Searches".
 *
 * Trade-off: requests fired without a path change between them share the
 * page's row, including on-page mutations. Callers that need a distinct
 * audit row for a specific action can call `regenerate(description)`
 * explicitly before the call.
 */
export class ActivityIdService {
	private static _instance: ActivityIdService | undefined;
	private _currentId: string;
	private _currentDescription: string | null = null;
	private _lastResolvedPath: string | null = null;
	private _resolver: DescriptionResolver | null = null;

	private constructor() {
		this._currentId = ActivityIdService._generateId();
		// Leave _lastResolvedPath null on purpose so the first
		// getCurrent() call always does a fresh resolution against the
		// current pathname. Setting it eagerly here would race with
		// module-load order: when libs/web-ui registers the resolver,
		// modules have not yet called ModuleRegistry.registerModule, so
		// any eager resolution at that point would return null and stick
		// for the rest of the session.
	}

	public static getInstance(): ActivityIdService {
		if (this._instance == null) {
			this._instance = new ActivityIdService();
		}

		return this._instance;
	}

	/**
	 * Register a resolver that maps a pathname to its friendly activity
	 * description. Called once at module-load time from the framework
	 * layer (e.g. libs/web-ui registers a ModuleRegistry-backed resolver),
	 * so the resolver function is in place before the first API call
	 * fires. Invalidates the lazy-rotation cache so the next getCurrent()
	 * call re-resolves against the now-registered resolver — important
	 * because modules typically register their routes after libs/web-ui
	 * is imported, meaning an eager resolution at registration time
	 * would see an empty ModuleRegistry.
	 */
	public setDescriptionResolver(resolver: DescriptionResolver | null): void {
		this._resolver = resolver;
		this._lastResolvedPath = null;
	}

	public getCurrent(): string {
		this._maybeRotate();
		return this._currentId;
	}

	public getCurrentDescription(): string | null {
		this._maybeRotate();
		return this._currentDescription;
	}

	/**
	 * Generate a new id and (optionally) attach a description. Useful for
	 * on-page mutations that should produce their own audit row instead
	 * of sharing the page's row. Marks the current path so the lazy
	 * rotation doesn't immediately overwrite the supplied description on
	 * the next getCurrent() call.
	 */
	public regenerate(description?: string | null): string {
		this._currentId = ActivityIdService._generateId();
		this._currentDescription = ActivityIdService._normalize(description);
		this._lastResolvedPath = ActivityIdService._readCurrentPath();

		return this._currentId;
	}

	/**
	 * Update the description without rotating the id. Useful for pages
	 * whose description depends on async state (e.g. resolving a product
	 * name to embed in "Viewed Product: Acme Widget").
	 */
	public setDescription(description: string | null): void {
		this._currentDescription = ActivityIdService._normalize(description);
	}

	private _maybeRotate(): void {
		const path = ActivityIdService._readCurrentPath();
		if (path == null) return;
		if (path === this._lastResolvedPath) return;

		this._lastResolvedPath = path;
		this._currentId = ActivityIdService._generateId();
		const search = ActivityIdService._readCurrentSearch();
		this._currentDescription = this._resolveDescription(path, search);
	}

	private _resolveDescription(pathname: string, search: string): string | null {
		if (this._resolver == null) return null;
		try {
			return ActivityIdService._normalize(this._resolver(pathname, search));
		} catch {
			return null;
		}
	}

	private static _readCurrentPath(): string | null {
		if (typeof window === 'undefined' || window.location == null) {
			return null;
		}
		return window.location.pathname;
	}

	private static _readCurrentSearch(): string {
		if (typeof window === 'undefined' || window.location == null) {
			return '';
		}
		return window.location.search ?? '';
	}

	private static _normalize(
		description: string | null | undefined,
	): string | null {
		return description != null && description !== '' ? description : null;
	}

	private static _generateId(): string {
		if (
			typeof crypto !== 'undefined' &&
			typeof crypto.randomUUID === 'function'
		) {
			return crypto.randomUUID();
		}

		// Fallback for environments without crypto.randomUUID — same shape as
		// the v4 polyfill used elsewhere in the codebase.
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;

			return v.toString(16);
		});
	}
}
